import {
  Badge,
  ChakraFlex,
  ChakraText,
  CircularProgress,
  EquipUIFireflyV2Theme,
  InfiniteScroll,
  UserAvatarCard,
  UserQuestion,
} from '@equip.health/ui';

import { GlobalSearchSuggestion } from '~/lib/types/navbar/global-search';
import { SearchResultsProps } from '../GlobalSearchAdmin/constants';

const { neutral } = EquipUIFireflyV2Theme.colors;

const LOADING_SIZE = 24;

const SearchResults = ({
  activeSuggestionIndex,
  fetchSuggestions,
  getListItemId,
  handleSelect,
  id,
  inputValue,
  isLoading,
  maxHeight,
  showUnknownIcon = false,
  suggestions,
  totalOptionsCount = 0,
}: SearchResultsProps) => {
  const listMaxHeight = maxHeight - (isLoading ? LOADING_SIZE : 0);
  return (
    <>
      {isLoading && (
        <ChakraFlex alignItems="center" justifyContent="center">
          <CircularProgress
            id={`${id}__loading-indicator`}
            isIndeterminate
            size={`${LOADING_SIZE}px`}
          />
        </ChakraFlex>
      )}
      {suggestions.length ? (
        <InfiniteScroll
          dataLength={suggestions.length}
          fetchData={(): void => {
            fetchSuggestions?.(inputValue, false);
          }}
          hideCount
          id={id}
          marginTop="4px"
          maxHeight={`${listMaxHeight}px`}
          showLoader={false}
          totalCount={totalOptionsCount}
        >
          {suggestions.map(
            (suggestion: GlobalSearchSuggestion, index: number) => {
              const {
                additionalPrimaryText,
                avatarSrc,
                badgeText,
                primaryText,
                secondaryText,
                value,
              } = suggestion;
              const listItemId = getListItemId(value);
              return (
                <ChakraFlex
                  _hover={{
                    backgroundColor: 'neutral.background.secondary',
                    cursor: 'pointer',
                  }}
                  align="center"
                  backgroundColor={
                    index === activeSuggestionIndex
                      ? 'neutral.background.secondary'
                      : ''
                  }
                  borderRadius="8px"
                  color="unset"
                  fontStyle="normal"
                  id={listItemId}
                  justify="space-between"
                  key={listItemId}
                  marginTop="2px"
                  onClick={() => handleSelect(suggestion)}
                  padding="16px"
                  role="listitem"
                >
                  <ChakraFlex align="center">
                    {showUnknownIcon && (
                      <ChakraFlex
                        align="center"
                        background="neutral.background.secondary"
                        borderColor="neutral.line"
                        borderRadius="61px"
                        borderWidth="4px"
                        height="40px"
                        justify="center"
                        marginRight="8px"
                        width="40px"
                      >
                        <UserQuestion
                          fill={neutral.primary}
                          height="17.14px"
                          width="100%"
                        />
                      </ChakraFlex>
                    )}
                    <UserAvatarCard
                      avatarSize="xl"
                      avatarSrc={avatarSrc}
                      hideAvatar={showUnknownIcon}
                      id={listItemId}
                      primaryText={
                        additionalPrimaryText
                          ? [primaryText, ...additionalPrimaryText]
                          : [primaryText]
                      }
                      secondaryText={secondaryText}
                      secondaryTextColor="neutral.secondary"
                    />
                  </ChakraFlex>
                  {badgeText && (
                    <Badge
                      badgeColor="neutral.line"
                      fontColor="neutral.secondary"
                      fontSize="10px"
                      fontWeight={700}
                      id={listItemId}
                      margin="0"
                    >
                      {badgeText}
                    </Badge>
                  )}
                </ChakraFlex>
              );
            },
          )}
        </InfiniteScroll>
      ) : (
        <ChakraText id={`${id}__no-results-text`}>No suggestions</ChakraText>
      )}
    </>
  );
};

export default SearchResults;
